import { Component, DestroyRef, OnInit, inject, signal, Signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { PhoneNumbers, PhoneNumberConfig, Networks } from '@app/_models/number.model';
import { AuthResponse } from '@app/_models/responses';
import { UserService, AuthenticationService } from '@app/_services';
import { DeviceService } from '@app/_services/device.service';
import { Property } from '@app/_models/propery.model';
import { environment } from '@environments/environment';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faSearch, faCheckCircle, faBan, faSms, faEnvelope, faPlusCircle, faPencilAlt, faQuestionCircle, faMapMarkedAlt, faUser, faTimesCircle, faEyeSlash, faEye, faPaperPlane, faSpinner, faMapPin, faMapSigns } from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-manage-numbers',
    templateUrl: './manage-numbers.component.html',
    styleUrls: ['./manage-numbers.component.css'],
    standalone: false
})
export class ManageNumbersComponent implements OnInit {

  faSearch = faSearch;
  faCheckCircle = faCheckCircle;
  faBan = faBan;
  faSms = faSms;
  faEnvelope = faEnvelope;
  faPlusCircle = faPlusCircle;
  faPencilAlt = faPencilAlt;
  faQuestionCircle = faQuestionCircle;
  faMapMarkedAlt = faMapMarkedAlt;
  faUser = faUser;
  faWhatsapp = faWhatsapp;
  faTimesCircle = faTimesCircle;
  faEyeSlash = faEyeSlash;
  faEye = faEye;
  faPaperPlane = faPaperPlane;
  faSpinner = faSpinner;
  faMapPin = faMapPin;
  faMapSigns = faMapSigns;

  numbers = signal<PhoneNumbers[]>([]);
  deviceConfigs = signal<PhoneNumberConfig[]>([]);
  networks = signal<Networks[]>([]);
  sequenceNumbers: number[] = Array.from({length: 200}, (_, i) => i + 1);

  help = signal(false);
  submitUpdate = signal(false);
  submitAdd = signal(false);
  submitQuery = signal(false);

  searchNumbers = signal('');
  loadDetail = signal<Record<number, boolean>>({});
  action = signal<string | null>(null);
  propertyid = signal<number | null>(null);
  private destroyRef = inject(DestroyRef);

  numberDetail = signal<PhoneNumbers | null>(null);
  updatePhoneNumberForm!: FormGroup;
  addPhoneNumberForm!: FormGroup;
  checkNumberAssigned!: FormGroup;

  currentUser = signal<AuthResponse | null>(null);
  p = signal(1);
  numberPattern = '[0-9]*';
  proplist = signal<Property[]>([]);

  constructor(private formBuilder: FormBuilder,
    private router: Router, private toastr: ToastrService,
    private route: ActivatedRoute, private deviceService: DeviceService,
    private userService: UserService, private authenticationService: AuthenticationService) { }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (params) => {
          this.action.set(params.get('action'));
          const propertyId = Number(params.get('propertyid'));
          this.propertyid.set(Number.isNaN(propertyId) ? null : propertyId);
          this.loadNumbersAndConfigs();
        }
      });

    this.deviceService.getCellularNetworks()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.networks.set(data);
        },
        error: (error) => {
          this.toastr.error('Failed to retrieve phone networks');
          if (!environment.production) {
            console.log(error);
          }
        }
      });

    this.authenticationService.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (x) => {
          this.currentUser.set(x || null);
          if (!this.currentUser()?.username) {
            this.proplist.set([]);
            return;
          }

          this.userService.getPropertiesByRoleAccessList(this.currentUser()!.username!, 'ROLE_ADMIN')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (data) => {
                this.proplist.set(data);
              },
              error: () => {
                this.toastr.error('Fail to retrieve properties.', 'Error');
              }
            });
        }
      });

    this.updatePhoneNumberForm = this.formBuilder.group({
      id: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phoneNumber: ['', [Validators.required, Validators.minLength(10)]],
      numberType: ['', Validators.required],
      configId: ['', Validators.required],
      network: ['', Validators.required],
      sequenceNumber: ['', Validators.required],
      apartmentNumber: ['', Validators.required],
      address: ['', Validators.required],
      status: ['', Validators.required]
    });

    this.checkNumberAssigned = this.formBuilder.group({
      property: ['', Validators.required],
      configId: ['', Validators.required],
      sequenceNumber: ['', [Validators.required, Validators.pattern(this.numberPattern)]]
    });

    this.addPhoneNumberForm = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phoneNumber: ['', [Validators.required, Validators.minLength(10)]],
      numberType: ['', Validators.required],
      configId: ['', Validators.required],
      network: ['', Validators.required],
      apartmentNumber: ['', Validators.required],
      address: ['', Validators.required],
      status: ['']
    });
    //this.updatePhoneNumberForm

  }

  private loadNumbersAndConfigs() {
    if (this.propertyid() == null) {
      this.numbers.set([]);
      this.deviceConfigs.set([]);
      return;
    }

    this.deviceService.getPhoneNumbersByPropertyId(this.propertyid()!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.numbers.set(data);
          if (!environment.production) {
            console.log('Data ret: ', this.numbers());
          }
        },
        error: (error) => {
          this.toastr.error('Failed to retrieve phone numbers');
          if (!environment.production) {
            console.log(error);
          }
        }
      });

    this.deviceService.getCallingDeviceByProperty(this.propertyid()!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.deviceConfigs.set(data);
          if (!environment.production) {
            console.log('Sim card configs: ', this.deviceConfigs());
          }
        },
        error: (error) => {
          this.toastr.error(error);
          if (!environment.production) {
            console.log(error);
          }
        }
      });
  }

  get fi() { return this.addPhoneNumberForm.controls; }
  get fq() { return this.checkNumberAssigned.controls; }
  get fa() { return this.checkNumberAssigned.controls; }
  get fe() { return this.updatePhoneNumberForm.controls; }

  leftPad(str: string, len: number, ch: string): string {
    // len = len - str.length + 1;
    // return len > 0 ?
    //   new Array(len).join(ch) + str : str;
    let s = str + "";
    while (s.length < len) {
      s = ch + s;
    }
    return s;
  }

  openDiv(id: number) {
    const currentNumbers = this.numbers();
    if (currentNumbers.find(r => r.id == id)?.opened) {
      const updated = currentNumbers.map(n =>
        n.id === id ? { ...n, opened: false } : n
      );
      this.numbers.set(updated);
    } else {
      const loadDetails = { ...this.loadDetail() };
      loadDetails[id] = true;
      this.loadDetail.set(loadDetails);

      this.numberDetail.set(currentNumbers.find(r => r.id == id) || null);
      if (this.numberDetail()) {
        this.numberDetail()!.opened = true;
        if (!environment.production) {
          console.log('Number detail output: ', this.numberDetail());
        }
      }

      loadDetails[id] = false;
      this.loadDetail.set(loadDetails);
    }
  }

  convertToCommand(phone: string, configid: number, sequence: string, action: string) {
    let command = "";
    let password = "";
    let version = "";
    let deviceinfo;
    let seq = sequence;

    seq = this.leftPad(seq, 3, "0");

    deviceinfo = this.deviceConfigs().find(element => element.id == configid);
    if (!environment.production) {
      console.log('configs loaded: ', this.deviceConfigs());
      console.log('config id: ',configid);
      console.log('device info: ', deviceinfo);
    }

    password = deviceinfo!.password || '';
    version = deviceinfo!.version || '';

    if (action == 'add') {
      if (version == "V1") {
        command = password + "A" + seq + "%23" + phone + "%23";
      }

      if (version == "V2") {
        command = password + "%23" + "TEL" + phone + "%23" + seq + "%23";
      }
    }

    if (action == 'remove') {
      if (version == "V1") {
        command = password + "A" + seq + "%23%23";
      }

      if (version == "V2") {
        command = password + "%23TEL%23" + seq + "%23";
      }
    }

    if (action == 'check') {
      if (version == "V1") {
        command = password + "A" + seq + "%23"
      }

      if (version == "V2") {
        command = password + "%23" + "TEL" + seq + "%3F"
      }
    }

    command = deviceinfo!.phoneNumber + "/?body=" + command;

    return command;
  }

  generateMessage(phone: string, configid: number) {
    let messagec = "";
    let deviceinfo;

    if (!environment.production) {
      console.log('phone: ',phone);
      console.log('config id: ',configid);
      console.log('Dev config: ')
    }

    deviceinfo = this.deviceConfigs().find(element => element.id == configid);

    messagec = "Your number has been added. %0a%0aYou can now call " + deviceinfo!.phoneNumber + " to open the gate at " + deviceinfo!.propertyName + ".  %0a%0aRegards, %0a" + deviceinfo!.propertyName + " Management." ;

    return messagec;
  }

  toggleHelp() {
    this.help.set(!this.help());
  }

  enableNumber(id: number, phone: string, configid: number, sequence: string) {
    let finalCommand = this.convertToCommand(phone, configid, sequence, 'add');

    //update flag
    this.onUpdateStatus(id);

    if (!environment.production) {
      console.log('Final command: ', finalCommand);
    }
    location.href = "sms://+1" + finalCommand;
  }

  disableNumber(id: number, phone: string, configid :number, sequence: string) {
    let finalCommand = this.convertToCommand(phone, configid, sequence, 'remove');

    //update flag
    this.onUpdateStatus(id);

    if (!environment.production) {
      console.log('Final command: ', finalCommand);
    }
    location.href = "sms://+1" + finalCommand;

  }

  updateNumber(id: number) {
    let numberobject;

    numberobject = this.numbers().find(t => t.id == id);

    if (!environment.production) {
      console.log('Number data: ', numberobject);
    }

    this.updatePhoneNumberForm.setValue(
      {
        id: numberobject!.id, firstName: numberobject!.firstName, lastName: numberobject!.lastName,
        phoneNumber: numberobject!.phoneNumber, numberType: numberobject!.numberType, network: numberobject!.network, apartmentNumber: numberobject!.apartmentNumber,
        configId: numberobject!.configId, sequenceNumber: numberobject!.sequenceNumber,
        address: numberobject!.address, status: numberobject!.status
      });
    this.router.navigate(['/manage-numbers/update/' + this.propertyid()]);
  }

  // addNumber(configid: number, sequence: string) {
  //   this.router.navigate(['/available-properties/manage-number'])
  // }

  addNumber() {
    this.router.navigate(['/available-properties/manage-number'])
  }

  queryNumber() {
    this.router.navigate(['/manage-numbers/query/' + this.propertyid()]);
  }

  notifyPerson(phone: string, configid: number, type: string) {
    let finalCommand = "";
    let message = this.generateMessage(phone, configid);

    //To-do: Update status logic here

    console.log("phone: ",phone);

    if (type == 'Whatsapp') {
      finalCommand = "https://wa.me/+1" + phone + "/?text=" + message;
    }

    if (type == 'SMS') {
      finalCommand = "sms://+1" + phone + "/?body=" + message;
    }


    if (!environment.production) {
      console.log('Message: ', message, 'Type: ', type);
      console.log('Command: ', finalCommand);
    }
    location.href = finalCommand;
  }

  onSubmit() {
    this.submitAdd.set(true);

    let data = this.addPhoneNumberForm.value;

    if (!environment.production) {
      console.log('Data submitted: New : ', data);
      console.log('Form object: ', this.addPhoneNumberForm);
    }

    if (this.addPhoneNumberForm.invalid) {
      return;
    }

    const currentNumbers = this.numbers();
    this.numbers.set([...currentNumbers, data]);

    this.deviceService.createPhoneNumber(data).subscribe({
      next: (response) => {
        this.toastr.success(response.message, 'Success');
        this.loadNumbersAndConfigs();
        this.router.navigate(['/manage-numbers/list/' + this.propertyid()]);
      },
      error: (error) => {
        this.toastr.error(error, 'Error');
      }
    });
  }

  onSubmitCheck() {
    this.submitQuery.set(true);

    if (this.checkNumberAssigned.invalid) {
      this.toastr.error("Please fill out form properly", "Error");
      return ;
    }

    let formdata = this.checkNumberAssigned.value;
    let finalCommand = this.convertToCommand("", formdata.configId, formdata.sequenceNumber, "check");
    if (!environment.production) {
      console.log('Final command: ', finalCommand);
    }
    location.href =  "sms://+1" + finalCommand;
  }

  onUpdateStatus(id: any) {

    if (!environment.production) {
      console.log('Data submitted: Update Status: ', id);
    }

    this.deviceService.updatePhoneNumberStatus(id).subscribe({
      next: (data) => {
        this.toastr.success(data.message, 'Success');
        this.loadNumbersAndConfigs();
        this.router.navigate(['/manage-numbers/list/' + this.propertyid()]);
        this.submitUpdate.set(false);
      },
      error: (error) => {
        this.toastr.error(error.message, 'Error');
        this.submitUpdate.set(false);
      }
    });



  }

  onDelete(id: number, phone: string, configid :number, sequence: string, status: boolean) {

    //check if enabled before calling this function below
    if (!environment.production) {
      console.log('Number status: ', status);
    }
    if (status) {
      //this.onUpdateStatus(id);
      this.disableNumber(id, phone, configid, sequence);
    }


    this.deviceService.deletePhoneNumber(id).subscribe({
      next: (data) => {
        this.toastr.success(data.message, 'Success');
        this.loadNumbersAndConfigs();
      },
      error: (error) => {
        this.toastr.error(error.message, 'Error');
      }
    });


  }

  onUpdate() {
    this.submitUpdate.set(true);

    var data = this.updatePhoneNumberForm.value;

    if (this.updatePhoneNumberForm.invalid)
    {
        return;
    }

    if (!environment.production) {
      console.log('Data submitted: Update : ', data);
    }

    this.deviceService.updatePhoneNumber(data).subscribe({
      next: (response) => {
        this.toastr.success(response.message, 'Success');
        this.loadNumbersAndConfigs();
        this.router.navigate(['/manage-numbers/list/' + this.propertyid()]);
        this.submitUpdate.set(false);
      },
      error: (error) => {
        this.toastr.error(error.message, 'Error');
        this.submitUpdate.set(false);
      }
    });

    if (!environment.production) {
      console.log('Data being updated: ', data);
    }

  }
}
